/* Wire formats. JSON for structure (snapshot, deltas, events), binary for the two hot paths:
   robot positions (only those inside a viewer's window that moved) and chunk dig bitmaps (run-length). */

export const BIN_CHUNK = 1, BIN_ROBOTS = 2;

export interface RobotPos { id: number; x: number; y: number; cls: number; st: number; left: boolean; carry: number; bat: number }

export function encodeRobots(list: RobotPos[], full: boolean): Uint8Array {
  const out = new Uint8Array(4 + list.length * 10);
  const dv = new DataView(out.buffer);
  out[0] = BIN_ROBOTS; out[1] = full ? 1 : 0; dv.setUint16(2, list.length, true);
  let o = 4;
  for (const r of list) {
    dv.setUint16(o, r.id, true); o += 2;
    dv.setUint16(o, Math.max(0, Math.min(65535, Math.round(r.x * 16))), true); o += 2;
    const yq = Math.max(0, Math.min(0xffffff, Math.round(r.y * 16)));
    out[o++] = yq & 255; out[o++] = (yq >> 8) & 255; out[o++] = yq >> 16;
    out[o++] = (r.cls & 7) | ((r.st & 7) << 3) | (r.left ? 64 : 0) | (r.carry ? 128 : 0);
    out[o++] = Math.max(0, Math.min(100, Math.round(r.bat)));
    out[o++] = r.carry & 255;
  }
  return out;
}
export function decodeRobots(buf: ArrayBuffer): { full: boolean; list: RobotPos[] } {
  const u = new Uint8Array(buf), dv = new DataView(buf), n = dv.getUint16(2, true), list: RobotPos[] = [];
  let o = 4;
  for (let i = 0; i < n; i++) {
    const id = dv.getUint16(o, true), x = dv.getUint16(o + 2, true) / 16, y = (u[o + 4] | (u[o + 5] << 8) | (u[o + 6] << 16)) / 16, f = u[o + 7];
    list.push({ id, x, y, cls: f & 7, st: (f >> 3) & 7, left: !!(f & 64), carry: u[o + 9], bat: u[o + 8] });
    o += 10;
  }
  return { full: !!u[1], list };
}

function pushVar(a: number[], v: number) { while (v >= 128) { a.push((v & 127) | 128); v >>>= 7; } a.push(v); }
/* dug bitmap -> alternating run lengths starting with "not dug" */
export function encodeChunk(k: number, ver: number, dug: Uint8Array): Uint8Array {
  const runs: number[] = [];
  let cur = 0, len = 0;
  for (let i = 0; i < dug.length; i++) { const v = dug[i] ? 1 : 0; if (v === cur) len++; else { pushVar(runs, len); cur = v; len = 1; } }
  pushVar(runs, len);
  const out = new Uint8Array(9 + runs.length), dv = new DataView(out.buffer);
  out[0] = BIN_CHUNK; dv.setUint32(1, k, true); dv.setUint32(5, ver, true); out.set(runs, 9);
  return out;
}
export function decodeChunk(buf: ArrayBuffer, size: number): { k: number; ver: number; dug: Uint8Array } {
  const u = new Uint8Array(buf), dv = new DataView(buf), k = dv.getUint32(1, true), ver = dv.getUint32(5, true), dug = new Uint8Array(size);
  let o = 9, p = 0, cur = 0;
  while (o < u.length && p < size) {
    let v = 0, s = 0, b: number;
    do { b = u[o++]; v |= (b & 127) << s; s += 7; } while (b & 128);
    if (cur) dug.fill(1, p, Math.min(size, p + v));
    p += v; cur ^= 1;
  }
  return { k, ver, dug };
}
/* same RLE without header, for database storage */
export const rleDug = (dug: Uint8Array) => encodeChunk(0, 0, dug).subarray(9);
export function unRleDug(rle: Uint8Array, size: number): Uint8Array {
  const buf = new Uint8Array(9 + rle.length); buf[0] = BIN_CHUNK; buf.set(rle, 9);
  return decodeChunk(buf.buffer, size).dug;
}

export interface FeedEvent { id: number; t: number; k: string; m: string; x?: number; y?: number }
